

import { useState, useEffect } from 'react';
import Pagination from "../components/Pagination.jsx";
import { motion } from 'framer-motion';
import { Trash2, Edit, Plus, RefreshCw, MessageSquare, AlertCircle, AlertTriangle, Check } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { CreateTesomonial, UpdateTesomonial, AllTesomonial, DeleteTesomonial } from '../components/Api.jsx';

export default function TestimonialManager() {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);

  const [testimonials, setTestimonials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false); // State for popup
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null });
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const fetchTestimonials = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setIsLoading(true);
      }
      const response = await AllTesomonial();
      setTestimonials(response.data || []);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setIsLoading(false);
      }
    }
  };

  const handleRefresh = () => {
    fetchTestimonials(true);
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const onSubmit = async (data) => {
    try {
      if (editingId) {
        await UpdateTesomonial(editingId, data.name, data.role, data.text, data.rating);
        showToast('Testimonial updated successfully');
      } else {
        await CreateTesomonial(data.name, data.role, data.text, data.rating);
        showToast('Testimonial created successfully');
      }
      reset();
      setEditingId(null);
      setIsPopupOpen(false); // Close popup after submission
      fetchTestimonials();
    } catch (error) {
      console.error('Error submitting testimonial:', error.response?.data || error.message);
      showToast('Failed to save testimonial. Please try again.', 'error');
    }
  };

  const handleEdit = (testimonial) => {
    setEditingId(testimonial.id);
    setValue('name', testimonial.name);
    setValue('role', testimonial.role);
    setValue('text', testimonial.text);
    setValue('rating', testimonial.rating);
    setIsPopupOpen(true); // Open popup for editing
  };

  const handleDelete = (id) => {
    setConfirmModal({
      show: true,
      title: 'Confirm Delete',
      message: 'Are you sure you want to delete this testimonial? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await DeleteTesomonial(id);
          showToast('Testimonial deleted successfully');
          fetchTestimonials();
        } catch (error) {
          console.error('Error deleting testimonial:', error);
          showToast('Failed to delete testimonial. Please try again.', 'error');
        } finally {
          setConfirmModal(prev => ({ ...prev, show: false }));
        }
      }
    });
  };

  const openPopup = () => {
    setEditingId(null);
    reset(); // Reset form for new entry
    setIsPopupOpen(true);
  };

  return (
    <div className="min-h-screen bg-white text-black p-4">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
          }`}>
          <div className="flex items-center gap-2">
            {toast.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <Check className="w-5 h-5" />}
            {toast.message}
          </div>
        </div>
      )}
      {/* Header */}
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col lg:flex-row justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-start gap-4">
                <div className="bg-yellow-100 p-3 rounded-lg">
                  <MessageSquare className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-3xl">
                    Add, edit, delete the feedback given by the users. Manage user testimonials and reviews to showcase customer satisfaction.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-all duration-200 disabled:cursor-not-allowed text-sm font-medium shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                aria-label={refreshing ? "Refreshing data" : "Refresh testimonials"}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
              <button
                onClick={openPopup}
                className="bg-blue-600 text-white px-4 py-2 flex items-center gap-2"
              >
                <Plus size={20} />
                Add New Testimonial
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Popup Form */}
      {isPopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md"
          >
            <h2 className="text-2xl font-bold mb-4">{editingId ? 'Edit Testimonial' : 'Add Testimonial'}</h2>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <input
                    {...register('name', { required: 'Name is required' })}
                    placeholder="Name"
                    className="w-full p-2 rounded bg-gray-100 text-black border border-gray-300"
                  />
                  {errors.name && (
                    <p className="text-red-600 text-sm mt-1">{errors.name.message}</p>
                  )}
                </div>
                <div>
                  <input
                    {...register('role', { required: 'Role is required' })}
                    placeholder="Role"
                    className="w-full p-2 rounded bg-gray-100 text-black border border-gray-300"
                  />
                  {errors.role && (
                    <p className="text-red-600 text-sm mt-1">{errors.role.message}</p>
                  )}
                </div>
              </div>
              <div>
                <textarea
                  {...register('text', { required: 'Text is required' })}
                  placeholder="Testimonial Text"
                  className="w-full p-2 rounded bg-gray-100 text-black border border-gray-300"
                  rows={3}
                />
                {errors.text && (
                  <p className="text-red-600 text-sm mt-1">{errors.text.message}</p>
                )}
              </div>
              <div>
                <label htmlFor="rating" className="block text-sm font-medium text-gray-700 mb-1">Rating :</label>
                <select
                  {...register('rating', {
                    required: 'Rating is required',
                    valueAsNumber: true
                  })}
                  className="w-full p-2 rounded bg-gray-100 text-black border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={1}>1 Star</option>
                  <option value={2}>2 Stars</option>
                  <option value={3}>3 Stars</option>
                  <option value={4}>4 Stars</option>
                  <option value={5}>5 Stars</option>
                </select>
                {errors.rating && (
                  <p className="text-red-600 text-sm mt-1">{errors.rating.message}</p>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsPopupOpen(false)}
                  className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded flex items-center gap-2"
                >
                  <Plus size={20} />
                  {editingId ? 'Update Testimonial' : 'Add Testimonial'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Testimonial List */}
      {isLoading ? (
        <div className="text-center py-10">Loading testimonials...</div>
      ) : testimonials.length === 0 ? (
        <p className="text-gray-600 text-center">No testimonials found</p>
      ) : (
        <div className="grid gap-6">
          {testimonials.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="border border-gray-100 shadow-sm hover:shadow-md rounded-lg p-6 flex items-center justify-between"
            >
              <div>
                <h3 className="text-xl font-semibold">{testimonial.name}</h3>
                <p className="text-gray-700 italic">{testimonial.role}</p>
                <p className="text-gray-800 mt-2">{testimonial.text}</p>
                <p className="text-sm text-gray-500 mt-1">Rating: {testimonial.rating}/5</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(testimonial)}
                  className="p-2 text-blue-600 hover:text-blue-500"
                  title="Edit testimonial"
                >
                  <Edit size={20} />
                </button>
                <button
                  onClick={() => handleDelete(testimonial.id)}
                  className="p-2 text-red-600 hover:text-red-500"
                  title="Delete testimonial"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Custom Confirm Modal */}
      {confirmModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999] p-4 backdrop-blur-sm transition-all duration-300">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl transform transition-all duration-300 scale-100 p-6 border border-gray-100">
            <div className="flex flex-col items-center text-center">
              <div className="bg-red-50 p-4 rounded-full mb-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {confirmModal.title}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-8">
                {confirmModal.message}
              </p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setConfirmModal(prev => ({ ...prev, show: false }))}
                  className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmModal.onConfirm}
                  className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl shadow-lg shadow-red-200 transition-all duration-200 font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
          {testimonials?.length > 0 && (

      <Pagination 
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={Math.ceil((Array.isArray(testimonials) ? testimonials.length : (testimonials?.length || 0)) / itemsPerPage)}
          totalElements={(Array.isArray(testimonials) ? testimonials.length : (testimonials?.length || 0))}
          pageSize={itemsPerPage}
          setPageSize={setItemsPerPage}
        />
      )}
</div>
  );
}